import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { brl, fmtDate } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { Wallet, Plus, Trash2, Pencil, ShoppingCart, PackageSearch } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { FormField } from "../components/ui/form-field";

const emptyExp = {
  name: "",
  category: "produto",
  quantity: 1,
  unit_price: 0,
  date: new Date().toISOString().slice(0, 10),
  supplier: "",
  notes: "",
};

export default function Finance() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [p, s, e] = await Promise.all([
      api.get(`/reports/payments?from=${from}&to=${to}`),
      api.get(`/reports/summary?from=${from}&to=${to}`),
      api.get(`/expenses?from=${from}&to=${to}`),
    ]);
    setRows(p.data);
    setSummary(s.data);
    setExpenses(e.data);
  }, [from, to]);
  useEffect(() => {
    load();
  }, [load]);

  const totalToPay = rows.reduce((s, r) => s + (r.total || 0), 0);

  async function removeExp(id) {
    if (!(await confirm({
      title: t("Excluir esta compra/despesa?"),
      destructive: true,
      confirmText: t("Excluir"),
    }))) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success(t("Despesa excluída"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Financeiro")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Wallet size={26} /> {t("Pagamentos & Faturamento")}
          </h1>
          <Link
            to="/finance/reports"
            data-testid="link-finance-reports"
            className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
          >
            {t("📊 Ver Relatórios Detalhados (Equipe/Colaborador/Cliente + PDF) →")}
          </Link>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-sm">
            <div className="text-slate-500 mb-1">{t("De")}</div>
            <DatePickerUS value={from} onChange={setFrom} />
          </label>
          <label className="text-sm">
            <div className="text-slate-500 mb-1">{t("Até")}</div>
            <DatePickerUS value={to} onChange={setTo} />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card label={t("Receita")} value={brl(summary?.revenue || 0)} />
        <Card label={t("A pagar (folha)")} value={brl(totalToPay)} />
        <Card
          label={t("Produtos / Despesas")}
          value={brl(summary?.expenses || 0)}
        />
        <Card label={t("Lucro estimado")} value={brl(summary?.profit || 0)} />
        <Card label={t("Limpezas finalizadas")} value={summary?.finished ?? "—"} />
      </div>

      <div className="cm-card p-3 sm:p-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="font-semibold flex items-center gap-2">
            <ShoppingCart size={18} /> {t("Produtos & compras")}
          </h2>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/kits-compra"
              data-testid="finance-templates-link"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <PackageSearch size={14} /> {t("Kits de compra")}
            </Link>
            <Button
              data-testid="expense-new-btn"
              onClick={() => {
                setEditing({ ...emptyExp });
                setOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus size={14} className="mr-1" /> {t("Nova compra")}
            </Button>
          </div>
        </div>
        {expenses.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            {t("Nenhuma compra registrada no período.")}
          </div>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="md:hidden space-y-3">
              {expenses.map((e) => (
                <div
                  key={e.id}
                  data-testid={`expense-card-${e.id}`}
                  className="rounded-xl border border-slate-200 p-3 bg-white"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{e.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{fmtDate(e.date)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-slate-900">{brl(e.total)}</div>
                      <div className="text-[11px] text-slate-500">{e.quantity}× {brl(e.unit_price)}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                        {t(e.category)}
                      </span>
                      {e.supplier && (
                        <span className="text-[11px] text-slate-500 truncate">· {e.supplier}</span>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setEditing(e); setOpen(true); }}
                        data-testid={`expense-edit-${e.id}`}
                        className="p-2.5 rounded-lg hover:bg-slate-100 text-slate-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        aria-label={t("Editar")}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => removeExp(e.id)}
                        data-testid={`expense-delete-${e.id}`}
                        className="p-2.5 rounded-lg hover:bg-red-50 text-red-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        aria-label={t("Excluir")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: tabela */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2 pr-3 font-medium">{t("Data")}</th>
                  <th className="py-2 pr-3 font-medium">{t("Produto / Item")}</th>
                  <th className="py-2 pr-3 font-medium">{t("Categoria")}</th>
                  <th className="py-2 pr-3 font-medium">{t("Fornecedor")}</th>
                  <th className="py-2 pr-3 font-medium text-right">{t("Qtd")}</th>
                  <th className="py-2 pr-3 font-medium text-right">
                    {t("Preço unit.")}
                  </th>
                  <th className="py-2 pr-3 font-medium text-right">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`expense-row-${e.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                  >
                    <td className="py-2.5 pr-3 text-slate-600">
                      {fmtDate(e.date)}
                    </td>
                    <td className="py-2.5 pr-3 font-medium">{e.name}</td>
                    <td className="py-2.5 pr-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                        {t(e.category)}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      {e.supplier || "—"}
                    </td>
                    <td className="py-2.5 pr-3 text-right">{e.quantity}</td>
                    <td className="py-2.5 pr-3 text-right">
                      {brl(e.unit_price)}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-semibold">
                      {brl(e.total)}
                    </td>
                    <td className="py-2.5 pr-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => {
                            setEditing(e);
                            setOpen(true);
                          }}
                          data-testid={`expense-edit-${e.id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => removeExp(e.id)}
                          data-testid={`expense-delete-${e.id}`}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <div className="cm-card p-5">
        <h2 className="font-semibold mb-3">{t("Pagamentos a colaboradores")}</h2>
        {rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            {t("Nenhum pagamento no período.")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2 pr-3 font-medium">{t("Colaborador")}</th>
                  <th className="py-2 pr-3 font-medium">Zelle / Venmo</th>
                  <th className="py-2 pr-3 font-medium text-right">{t("Casas")}</th>
                  <th className="py-2 pr-3 font-medium text-right">{t("Dias")}</th>
                  <th className="py-2 pr-3 font-medium text-right">
                    {t("Valor / dia")}
                  </th>
                  <th className="py-2 pr-3 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.user_id}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                  >
                    <td className="py-2.5 pr-3 font-medium">{r.name}</td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      {r.zelle_venmo || "—"}
                    </td>
                    <td className="py-2.5 pr-3 text-right">{r.casas}</td>
                    <td className="py-2.5 pr-3 text-right">{r.days_worked ?? "—"}</td>
                    <td className="py-2.5 pr-3 text-right">
                      {brl(r.valor_por_dia)}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-semibold">
                      {brl(r.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ExpenseDialog
        open={open}
        onOpenChange={setOpen}
        item={editing}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />
    </AdminLayout>
  );
}

function Card({ label, value }) {
  return (
    <div className="cm-card p-3 sm:p-5 min-w-0">
      <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 font-semibold leading-tight">
        {label}
      </div>
      <div className="mt-1 sm:mt-2 text-lg sm:text-3xl font-bold font-[Outfit] truncate">{value}</div>
    </div>
  );
}

function ExpenseDialog({ open, onOpenChange, item, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(item || emptyExp);
  useEffect(() => {
    setForm(item || emptyExp);
  }, [item, open]);
  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  async function save() {
    try {
      const body = {
        ...form,
        quantity: parseFloat(form.quantity) || 0,
        unit_price: parseFloat(form.unit_price) || 0,
        date: form.date
          ? new Date(form.date).toISOString()
          : new Date().toISOString(),
      };
      if (item?.id) await api.patch(`/expenses/${item.id}`, body);
      else await api.post("/expenses", body);
      toast.success(t("Despesa registrada"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  const total =
    (parseFloat(form.quantity) || 0) * (parseFloat(form.unit_price) || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle>
            {item?.id ? t("Editar compra") : t("Nova compra / produto")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label={t("Produto / Item")}>
            
            <input
              data-testid="expense-field-name"
              className="cm-input"
              placeholder={t("Ex: Detergente, Pano de microfibra…")}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
</FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("Categoria")}>
              
              <select
                className="cm-input"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                <option value="produto">{t("Produto de limpeza")}</option>
                <option value="equipamento">{t("Equipamento")}</option>
                <option value="uniforme">{t("Uniforme / EPI")}</option>
                <option value="outro">{t("Outro")}</option>
              </select>
</FormField>
            <FormField label={t("Data")}>
              <DatePickerUS
                testId="expense-field-date"
                value={form.date?.slice(0, 10) || ""}
                onChange={(v) => set("date", v)}
              />
</FormField>
          </div>
          <FormField label={t("Fornecedor")}>
            
            <input
              className="cm-input"
              placeholder={t("Ex: Walmart, Costco, Home Depot…")}
              value={form.supplier || ""}
              onChange={(e) => set("supplier", e.target.value)}
            />
</FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("Quantidade")}>
              
              <input
                data-testid="expense-field-quantity"
                type="number"
                step="0.01"
                min="0"
                className="cm-input"
                value={form.quantity}
                onChange={(e) => set("quantity", e.target.value)}
              />
</FormField>
            <FormField label={t("Preço Unitário (US$)")}>
              
              <input
                data-testid="expense-field-price"
                type="number"
                step="0.01"
                min="0"
                className="cm-input"
                value={form.unit_price}
                onChange={(e) => set("unit_price", e.target.value)}
              />
</FormField>
          </div>
          <FormField label={t("Observações")}>
            
            <textarea
              rows={2}
              className="cm-input"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
            />
</FormField>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="text-sm text-slate-600">{t("Total da compra")}</span>
            <span className="text-xl font-bold text-slate-900">
              {brl(total)}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancelar")}
          </Button>
          <Button
            data-testid="expense-save-btn"
            onClick={save}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t("Salvar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
